import { readCommunity } from "@/features/community/records";
import type {
  WikiPageListPublic,
  WikiPagePublic,
} from "@softmax/observatory-client/types";
import { type PreviewMediaItem } from "../content/preview-details.types";
export async function readWikiImages({
  signal,
  wikiName,
}: {
  readonly signal?: AbortSignal | undefined;
  readonly wikiName: string;
}): Promise<PreviewMediaItem[]> {
  const list = await readCommunity<WikiPageListPublic>({
    signal,
    path: `/v2/wikis/${encodeURIComponent(wikiName)}/pages`,
  });

  if (list.status !== "ok") return [];

  const pages = list.data.entries.slice(0, maxWikiPagesRead);
  const bodies = await Promise.all(
    pages.map(async (page) => ({
      title: page.title,
      result: await readCommunity<WikiPagePublic>({
        signal,
        path: `/v2/wikis/${encodeURIComponent(wikiName)}/pages/${encodeURIComponent(page.slug)}`,
      }),
    })),
  );
  const items: PreviewMediaItem[] = [];
  bodies.forEach(({ result, title }) => {
    if (result.status !== "ok") return;

    for (const { src, alt } of imagesInWikiBody(
      result.data.current_revision.body,
    )) {
      const safe = safeWikiImageSrc(src);

      if (safe) items.push({ kind: "wiki", src: safe, alt: alt || title });
    }
  });
  return items;
}
const postMediaOrigin = "https://softmax-public.s3.amazonaws.com";
const postMediaPathPrefix = "/post-media/";
function safeWikiImageSrc(src: string): string | null {
  let url: URL;

  try {
    url = new URL(src.trim());
  } catch {
    return null;
  }

  if (url.origin !== postMediaOrigin) return null;

  if (!url.pathname.startsWith(postMediaPathPrefix)) return null;

  return url.href;
}
const markdownImagePattern = /!\[([^\]]*)\]\(([^)\s]+)(?:\s+"[^"]*")?\)/g;
const htmlImgPattern = /<img\s[^>]*?src=["']([^"']+)["'][^>]*?>/gi;
const htmlImgAltPattern = /\balt=["']([^"']*)["']/i;
function imagesInWikiBody(body: string): {
  src: string;
  alt: string;
}[] {
  const out: {
    src: string;
    alt: string;
  }[] = [];

  body.replace(markdownImagePattern, (match, alt: string, src: string) => {
    out.push({ src, alt: alt.trim() });
    return match;
  });

  body.replace(htmlImgPattern, (match, src: string) => {
    out.push({
      src,
      alt: htmlImgAltPattern.exec(match)?.[1]?.trim() ?? "",
    });
    return match;
  });

  return out;
}
const maxWikiPagesRead = 6;
